import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  applyColorMode,
  persistColorMode,
  readStoredColorMode,
  resolveColorMode,
} from '../utils/theme';

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [colorMode, setColorModeState] = useState(readStoredColorMode);
  const resolvedMode = useMemo(() => resolveColorMode(colorMode), [colorMode]);

  useEffect(() => {
    applyColorMode(colorMode);
  }, [colorMode]);

  useEffect(() => {
    if (colorMode !== 'system') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyColorMode('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [colorMode]);

  const setColorMode = (mode) => {
    setColorModeState(mode);
    persistColorMode(mode);
  };

  const value = useMemo(
    () => ({ colorMode, setColorMode, resolvedMode }),
    [colorMode, resolvedMode],
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
